import { initializeApp } from 'firebase/app';
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber, ConfirmationResult } from 'firebase/auth';
import { config } from './config';

// Extend window interface for reCAPTCHA
declare global {
  interface Window {
    grecaptcha: any;
  }
}

// Firebase configuration
const firebaseConfig = {
  apiKey: config.firebase.apiKey,
  authDomain: config.firebase.authDomain,
  projectId: config.firebase.projectId,
  storageBucket: config.firebase.storageBucket,
  messagingSenderId: config.firebase.messagingSenderId,
  appId: config.firebase.appId,
  measurementId: config.firebase.measurementId,
};

// Lazy initialize Firebase to avoid loading on every page
let app: any = null;
let auth: any = null;

const initializeFirebase = () => {
  if (typeof window === 'undefined') {
    console.warn('Firebase initialization skipped: not in browser environment');
    return { app: null, auth: null };
  }

  if (!app) {
    try {
      app = initializeApp(firebaseConfig);
      auth = getAuth(app);
      // Configure auth settings
      auth.languageCode = 'en';
      console.log('Firebase initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Firebase:', error);
      return { app: null, auth: null };
    }
  }
  return { app, auth };
};

// Export lazy-initialized auth
export const getFirebaseAuth = () => {
  const { auth } = initializeFirebase();
  return auth;
};


// Global variables for reCAPTCHA and confirmation result
let recaptchaVerifier: RecaptchaVerifier | null = null;
let confirmationResult: ConfirmationResult | null = null;
let recaptchaWidgetId: number | null = null;
let isRecaptchaInitializing = false;
let isSendingOTP = false;

/**
 * Initialize reCAPTCHA verifier with synchronization
 */
export const initializeRecaptcha = async (containerId: string = 'recaptcha-container'): Promise<RecaptchaVerifier | null> => {
  if (typeof window === 'undefined') return null;

  // Check if container exists before initializing
  const container = document.getElementById(containerId);
  if (!container) {
    console.warn(`reCAPTCHA container '${containerId}' not found. Skipping initialization.`);
    return null;
  }

  // Prevent concurrent initialization
  if (isRecaptchaInitializing) {
    console.log('reCAPTCHA initialization already in progress, waiting...');
    // Wait for any ongoing initialization to complete
    while (isRecaptchaInitializing) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return recaptchaVerifier;
  }

  isRecaptchaInitializing = true;
  console.log('🔄 Initializing reCAPTCHA...');

  try {
    // Force clear everything first
    await forceClearRecaptcha(containerId);

    // Create new reCAPTCHA verifier
    const auth = getFirebaseAuth();
    recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
      size: 'invisible',
      callback: (response: string) => {
        console.log('✅ reCAPTCHA solved successfully');
      },
      'expired-callback': () => {
        console.log('⚠️ reCAPTCHA expired, clearing...');
        clearRecaptcha();
      },
    });

    console.log('✅ reCAPTCHA initialized successfully');
    return recaptchaVerifier;

  } catch (error) {
    console.error('❌ Error creating reCAPTCHA verifier:', error);
    // Try one more time with a fresh start
    try {
      console.log('🔄 Retrying reCAPTCHA initialization...');
      await forceClearRecaptcha(containerId);

      const auth = getFirebaseAuth();
      recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
        size: 'invisible',
        callback: (response: string) => {
          console.log('✅ reCAPTCHA solved successfully (retry)');
        },
        'expired-callback': () => {
          console.log('⚠️ reCAPTCHA expired, clearing...');
          clearRecaptcha();
        },
      });

      console.log('✅ reCAPTCHA initialized successfully on retry');
      return recaptchaVerifier;

    } catch (retryError) {
      console.error('❌ reCAPTCHA initialization failed even on retry:', retryError);
      throw new Error('Failed to initialize reCAPTCHA. Please refresh the page and try again.');
    }
  } finally {
    isRecaptchaInitializing = false;
  }
};

/**
 * Force clear all reCAPTCHA elements and instances
 */
const forceClearRecaptcha = async (containerId: string = 'recaptcha-container'): Promise<void> => {
  console.log('🧹 Force clearing reCAPTCHA...');

  // Clear Firebase verifier
  if (recaptchaVerifier) {
    try {
      await recaptchaVerifier.clear();
      recaptchaVerifier = null;
      console.log('Cleared Firebase reCAPTCHA verifier');
    } catch (error) {
      console.warn('Error clearing Firebase verifier:', error);
    }
  }

  // Clear Google reCAPTCHA widget
  if (recaptchaWidgetId && typeof window !== 'undefined' && window.grecaptcha) {
    try {
      window.grecaptcha.reset(recaptchaWidgetId);
      console.log('Reset Google reCAPTCHA widget');
    } catch (error) {
      console.warn('Error resetting Google widget:', error);
    }
  }

  // Clear container element with delay to ensure DOM updates
  const container = document.getElementById(containerId);
  if (container) {
    container.innerHTML = '';
    // Add a small delay to ensure DOM is updated
    await new Promise(resolve => setTimeout(resolve, 100));
    console.log('Cleared reCAPTCHA container element');
  }

  // Reset widget ID
  recaptchaWidgetId = null;
};

/**
 * Send OTP to phone number with improved error handling
 */
export const sendOTP = async (phoneNumber: string): Promise<ConfirmationResult> => {
  // Prevent concurrent OTP requests
  if (isSendingOTP) {
    console.log('OTP request already in progress, please wait...');
    throw new Error('Please wait for the current request to complete.');
  }

  isSendingOTP = true;

  try {
    console.log('📱 Starting OTP request');

    // Initialize/reinitialize reCAPTCHA
    await initializeRecaptcha();

    if (!recaptchaVerifier) {
      throw new Error('Failed to initialize reCAPTCHA. Please refresh the page.');
    }

    // Format phone number with country code if not already present
    const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+91${phoneNumber}`;

    console.log('📤 Sending OTP to:', formattedPhone);

    // Send OTP with timeout
    const auth = getFirebaseAuth();
    const otpPromise = signInWithPhoneNumber(auth, formattedPhone, recaptchaVerifier);
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('OTP request timed out. Please try again.')), 30000)
    );

    confirmationResult = await Promise.race([otpPromise, timeoutPromise]) as ConfirmationResult;

    console.log('✅ OTP sent successfully');
    return confirmationResult;

  } catch (error: any) {
    console.error('❌ Error sending OTP:', error);

    // Handle specific error types
    if (error.message && error.message.includes('reCAPTCHA')) {
      console.log('🔄 reCAPTCHA error detected, attempting recovery...');

      // Try recovery for reCAPTCHA errors
      try {
        await forceClearRecaptcha();
        await new Promise(resolve => setTimeout(resolve, 1500)); // Longer wait

        await initializeRecaptcha();

        if (recaptchaVerifier) {
          const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+91${phoneNumber}`;
          console.log('🔄 Retrying OTP send after reCAPTCHA recovery...');

          const auth = getFirebaseAuth();
          confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, recaptchaVerifier);
          console.log('✅ OTP sent successfully after recovery');
          return confirmationResult;
        }
      } catch (recoveryError) {
        console.error('❌ Recovery failed:', recoveryError);
        await forceClearRecaptcha(); // Clean up on failure
      }
    }

    // Throw the original error if recovery didn't work
    throw error;

  } finally {
    isSendingOTP = false;
  }
};

/**
 * Verify OTP and get ID token
 */
export const verifyOTP = async (otp: string): Promise<string> => {
  try {
    if (!confirmationResult) {
      throw new Error('No OTP verification in progress. Please request OTP first.');
    }

    const result = await confirmationResult.confirm(otp);
    const user = result.user;

    // Get ID token
    const idToken = await user.getIdToken();

    return idToken;
  } catch (error) {
    console.error('Error verifying OTP:', error);
    throw error;
  }
};

/**
 * Sign out current user
 */
export const signOut = async () => {
  try {
    const auth = getFirebaseAuth();
    await auth.signOut();
    confirmationResult = null;
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

/**
 * Get current user
 */
export const getCurrentUser = () => {
  const auth = getFirebaseAuth();
  return auth.currentUser;
};

/**
 * Listen to auth state changes
 */
export const onAuthStateChanged = (callback: (user: any) => void) => {
  const auth = getFirebaseAuth();
  return auth.onAuthStateChanged(callback);
};

/**
 * Refresh ID token
 */
export const refreshToken = async (): Promise<string | null> => {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (user) {
    return await user.getIdToken(true);
  }
  return null;
};

/**
 * Clear reCAPTCHA completely (public interface)
 */
export const clearRecaptcha = async () => {
  await forceClearRecaptcha();
};

/**
 * Render reCAPTCHA widget (for debugging)
 */
export const renderRecaptcha = async (containerId: string = 'recaptcha-container') => {
  if (!recaptchaVerifier) {
    initializeRecaptcha(containerId);
  }

  if (recaptchaVerifier) {
    try {
      const widgetId = await recaptchaVerifier.render();
      recaptchaWidgetId = widgetId;
      console.log('reCAPTCHA rendered with widget ID:', widgetId);
      return widgetId;
    } catch (error) {
      console.error('Error rendering reCAPTCHA:', error);
      throw error;
    }
  }
};

export default app;
